import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { rootUrl } from '../../../../config/config';
import { useAuth } from '../../AuthContext';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;
    return `KES ${numeric.toLocaleString('en-KE')}`;
};

const normalizeDriverRows = (payload) => {
    const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    return rows;
};

const normalizeCarRows = (payload) => {
    return Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
};

export default function AllocationsPage() {
    const { token, user } = useAuth();
    const [allocations, setAllocations] = useState([]);
    const [driversById, setDriversById] = useState({});
    const [carsByPlate, setCarsByPlate] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [busyAllocation, setBusyAllocation] = useState('');

    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            try {
                const headers = { Authorization: `Bearer ${token}` };
                const [allocationsRes, driversRes, carsRes] = await Promise.all([
                    axios.get(`${rootUrl}/cars/drivers`, { headers }),
                    axios.get(`${rootUrl}/drivers?limit=1000`, { headers }),
                    axios.get(`${rootUrl}/cars`, { headers })
                ]);

                const driverMap = {};
                normalizeDriverRows(driversRes.data).forEach((driver) => {
                    if (driver?.id_no) driverMap[String(driver.id_no)] = driver;
                });

                const carMap = {};
                normalizeCarRows(carsRes.data).forEach((car) => {
                    if (car?.plate_no) carMap[String(car.plate_no)] = car;
                });

                const allocationRows = Array.isArray(allocationsRes.data) ? allocationsRes.data : [];
                setAllocations(allocationRows);
                setDriversById(driverMap);
                setCarsByPlate(carMap);
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Failed to load allocations');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token]);

    const enrichedAllocations = useMemo(() => {
        return allocations.map((allocation) => {
            const driver = driversById[String(allocation.driver_id)] || {};
            const car = carsByPlate[String(allocation.plate_no)] || {};
            const active = !allocation.end_date;

            return {
                ...allocation,
                driver_name: allocation.driver_name || driver.name || 'Unknown driver',
                driver_phone: driver.phone || '',
                owner_code: car.owner_code || allocation.owner_code || '',
                owner_name: car.owner_name || '',
                active
            };
        });
    }, [allocations, driversById, carsByPlate]);

    const filteredAllocations = useMemo(() => {
        const term = search.trim().toLowerCase();

        return enrichedAllocations.filter((allocation) => {
            if (statusFilter === 'active' && !allocation.active) return false;
            if (statusFilter === 'ended' && allocation.active) return false;

            if (!term) return true;

            return [
                allocation.plate_no,
                allocation.driver_id,
                allocation.driver_name,
                allocation.owner_code,
                allocation.owner_name,
                allocation.allocation_number
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(term));
        });
    }, [enrichedAllocations, search, statusFilter]);

    const stats = useMemo(() => {
        const activeCount = enrichedAllocations.filter((allocation) => allocation.active).length;
        const endedCount = enrichedAllocations.length - activeCount;
        return {
            total: enrichedAllocations.length,
            active: activeCount,
            ended: endedCount
        };
    }, [enrichedAllocations]);

    const handleDeallocate = async (allocation) => {
        if (!allocation?.allocation_number || !token || busyAllocation) return;

        const endDate = window.prompt('Enter deallocation date (YYYY-MM-DD)', new Date().toISOString().split('T')[0]);
        if (!endDate) return;

        const confirmed = window.confirm(`Deallocate ${allocation.driver_name} from ${allocation.plate_no} on ${endDate}?`);
        if (!confirmed) return;

        try {
            setBusyAllocation(String(allocation.allocation_number));
            await axios.put(
                `${rootUrl}/cars/drivers/${allocation.allocation_number}/deallocate`,
                {
                    end_date: endDate,
                    edit_by: user?.username || 'system'
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setAllocations((current) => current.map((row) => (
                String(row.allocation_number) === String(allocation.allocation_number)
                    ? { ...row, end_date: endDate }
                    : row
            )));
        } catch (err) {
            window.alert(err.response?.data?.message || err.message || 'Failed to deallocate allocation');
        } finally {
            setBusyAllocation('');
        }
    };

    return (
        <div className="min-h-screen bg-brand-gradient px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-7xl space-y-6">
                <div className="rounded-2xl border border-white/60 bg-white/90 p-6 shadow-sm backdrop-blur">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-primary">Driver Allocations</p>
                            <h1 className="mt-2 text-3xl font-bold text-slate-900">Vehicle assignment overview</h1>
                            <p className="mt-2 max-w-2xl text-sm text-slate-600">
                                Review active and historical driver allocations, jump to linked records, and deallocate current assignments when needed.
                            </p>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="rounded-xl bg-slate-50 px-4 py-3">
                                <div className="text-xs uppercase tracking-wide text-slate-500">Total</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{stats.total}</div>
                            </div>
                            <div className="rounded-xl bg-emerald-50 px-4 py-3">
                                <div className="text-xs uppercase tracking-wide text-emerald-700">Active</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{stats.active}</div>
                            </div>
                            <div className="rounded-xl bg-slate-100 px-4 py-3">
                                <div className="text-xs uppercase tracking-wide text-slate-500">Ended</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-700">{stats.ended}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 md:flex-row">
                        <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search by plate, driver, owner, or allocation number"
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                        />
                        <select
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                        >
                            <option value="all">All statuses</option>
                            <option value="active">Active only</option>
                            <option value="ended">Ended only</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    {loading ? (
                        <div className="flex items-center justify-center px-6 py-16 text-sm text-slate-500">Loading allocations...</div>
                    ) : error ? (
                        <div className="px-6 py-16 text-center text-sm text-red-600">{error}</div>
                    ) : filteredAllocations.length === 0 ? (
                        <div className="px-6 py-16 text-center text-sm text-slate-500">No allocations matched the current filters.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                    <tr>
                                        <th className="px-4 py-3">Allocation</th>
                                        <th className="px-4 py-3">Vehicle</th>
                                        <th className="px-4 py-3">Driver</th>
                                        <th className="px-4 py-3">Owner</th>
                                        <th className="px-4 py-3">Start</th>
                                        <th className="px-4 py-3">End</th>
                                        <th className="px-4 py-3">Weekly Amount</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredAllocations.map((allocation) => (
                                        <tr key={allocation.allocation_number} className="align-top hover:bg-slate-50/80">
                                            <td className="px-4 py-4">
                                                <div className="font-medium text-slate-900">{allocation.allocation_number}</div>
                                                <div className="text-xs text-slate-500">{allocation.notes || 'No notes'}</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <Link to={`/cars/${allocation.plate_no}`} className="font-semibold text-brand-primary hover:underline">
                                                    {allocation.plate_no}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-4">
                                                <Link to={`/drivers/${allocation.driver_id}`} className="font-medium text-slate-900 hover:text-brand-primary hover:underline">
                                                    {allocation.driver_name}
                                                </Link>
                                                <div className="text-xs text-slate-500">{allocation.driver_id}</div>
                                                {allocation.driver_phone ? <div className="text-xs text-slate-500">{allocation.driver_phone}</div> : null}
                                            </td>
                                            <td className="px-4 py-4">
                                                {allocation.owner_code ? (
                                                    <Link to={`/owners/timeline/${allocation.owner_code}`} className="font-medium text-slate-900 hover:text-brand-primary hover:underline">
                                                        {allocation.owner_name || allocation.owner_code}
                                                    </Link>
                                                ) : (
                                                    <span className="text-slate-500">-</span>
                                                )}
                                                {allocation.owner_name && allocation.owner_code ? (
                                                    <div className="text-xs text-slate-500">{allocation.owner_code}</div>
                                                ) : null}
                                            </td>
                                            <td className="px-4 py-4 text-slate-700">{formatDate(allocation.start_date || allocation.allocation_date)}</td>
                                            <td className="px-4 py-4 text-slate-700">{allocation.end_date ? formatDate(allocation.end_date) : '-'}</td>
                                            <td className="px-4 py-4 text-slate-700">{formatCurrency(allocation.weekly_amount)}</td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${allocation.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                                                    {allocation.active ? 'Active' : 'Ended'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                {allocation.active ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeallocate(allocation)}
                                                        disabled={busyAllocation === String(allocation.allocation_number)}
                                                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        {busyAllocation === String(allocation.allocation_number) ? 'Updating...' : 'Deallocate'}
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-slate-400">Closed</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
